import React, { useState } from 'react';
import styled from 'styled-components';
import { colors } from '@/constants/colors';
import {
   SettingsField,
   SettingsLbl,
   SettingsInput,
} from './ispSettingsUI';
import FileUploader from '@/components/Admin/UI/FileUploader';
import {
   ID_TYPES,
   ID_TYPE_LABEL,
   type IspCustomer,
   type IspIdType,
} from './ispMockData';

/**
 * Add or edit a CUSTOMER — the person or business paying, not their connection.
 *
 * ⚠️ WIDE, NOT TALL. Ray's standing rule, and I broke it first time round: eight
 * full-width fields stacked into a modal taller than the screen. Two columns of
 * compact fields fits the same form in half the height, and the panel's own
 * settings density is what makes it look like part of the product rather than
 * something bolted on.
 *
 * NO STATUS FIELD. A customer's lifecycle is an approval — pending, approved,
 * rejected — and an operator typing one into their own dashboard has approved
 * them by doing so. Pending exists only for someone who signed up through a
 * public form. Suspended and disconnected are ACCOUNT states, because one branch
 * of a business can be cut off while another keeps running (Ray, 2026-08-28).
 *
 * NO DUE DAY EITHER. Billing dates belong to an ACCOUNT (Ray, 2026-08-28):
 * Resto 1 and Resto 2 can be installed months apart and bill on different days,
 * so a single date on the person would be wrong for at least one of them.
 *
 * The customer code is not asked for either — it is generated (Ray,
 * 2026-08-28). A field whose value the operator cannot choose is a field that
 * only offers them a way to get it wrong.
 *
 * The PPPoE login is deliberately absent. A customer can hold several accounts —
 * Aztech's restaurant owner with Resto 1 and Resto 2 — so credentials belong to
 * an account, not to a person.
 */
export interface CustomerDraft {
   fullName: string;
   phone: string;
   email: string;
   address: string;
   idType: IspIdType | '';
   idPhotoUrl: string;
   notes: string;
}

interface Props {
   existing?: IspCustomer | null;
   onCancel: () => void;
   onSave: (draft: CustomerDraft) => void;
}

const IspCustomerForm: React.FC<Props> = ({ existing, onCancel, onSave }) => {
   const [form, setForm] = useState({
      fullName: existing?.fullName ?? '',
      phone: existing?.phone ?? '',
      email: existing?.email ?? '',
      address: existing?.address ?? '',
      notes: existing?.notes ?? '',
   });
   const [idType, setIdType] = useState<IspIdType | ''>(existing?.idType ?? '');
   const [idPhotoUrl, setIdPhotoUrl] = useState(existing?.idPhotoUrl ?? '');
   const [uploading, setUploading] = useState(false);
   const [errors, setErrors] = useState<Record<string, boolean>>({});
   const [message, setMessage] = useState('');

   const set = (e: React.ChangeEvent<HTMLInputElement>) =>
      setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

   const submit = () => {
      const next: Record<string, boolean> = {};
      if (!form.fullName.trim()) next.fullName = true;
      setErrors(next);
      if (Object.keys(next).length) {
         setMessage('Fill in the highlighted fields.');
         return;
      }
      setMessage('');
      onSave({
         fullName: form.fullName.trim(),
         phone: form.phone.trim(),
         email: form.email.trim(),
         address: form.address.trim(),
         idType,
         idPhotoUrl,
         notes: form.notes.trim(),
      });
   };

   return (
      <Wrapper>
         <Grid>
            {/* Paired so every cell is filled. A $wide field mid-grid pushes the
                one before it onto a row of its own and leaves a hole beside it,
                which is what this looked like first time. The uploader goes last
                and full width: it is the only control here that is tall, and
                beside a one-line select it left a block of empty space. */}
            <SettingsField>
               <SettingsLbl>Full name</SettingsLbl>
               <Field
                  name="fullName"
                  value={form.fullName}
                  placeholder="e.g. Marites Bautista"
                  $err={!!errors.fullName}
                  onChange={set}
               />
            </SettingsField>

            <SettingsField>
               <SettingsLbl>Phone</SettingsLbl>
               <Field
                  name="phone"
                  value={form.phone}
                  placeholder="0917 555 0000"
                  onChange={set}
               />
            </SettingsField>

            <SettingsField>
               <SettingsLbl>Email (optional)</SettingsLbl>
               <Field
                  name="email"
                  value={form.email}
                  placeholder="name@example.com"
                  onChange={set}
               />
            </SettingsField>

            <SettingsField>
               <SettingsLbl>Address</SettingsLbl>
               <Field
                  name="address"
                  value={form.address}
                  placeholder="e.g. 14 Mabini St, Cabanatuan"
                  onChange={set}
               />
            </SettingsField>

            <SettingsField>
               <SettingsLbl>ID type</SettingsLbl>
               <Sel
                  value={idType}
                  onChange={(e) => setIdType(e.target.value as IspIdType | '')}
               >
                  <option value="">Not provided</option>
                  {ID_TYPES.map((t) => (
                     <option key={t} value={t}>
                        {ID_TYPE_LABEL[t]}
                     </option>
                  ))}
               </Sel>
            </SettingsField>

            <SettingsField>
               <SettingsLbl>Notes (optional)</SettingsLbl>
               <Field
                  name="notes"
                  value={form.notes}
                  placeholder="Anything worth remembering"
                  onChange={set}
               />
            </SettingsField>

            <SettingsField $wide>
               <SettingsLbl>ID photo</SettingsLbl>
               <FileUploader
                  value={idPhotoUrl}
                  onChange={setIdPhotoUrl}
                  accept="image/*,application/pdf"
                  compact
                  onUploadingChange={setUploading}
               />
            </SettingsField>
         </Grid>

         {message && <Err>{message}</Err>}

         <Actions>
            <Ghost type="button" onClick={onCancel}>
               Cancel
            </Ghost>
            <Primary type="button" disabled={uploading} onClick={submit}>
               {uploading
                  ? 'Uploading…'
                  : existing
                    ? 'Save changes'
                    : 'Add customer'}
            </Primary>
         </Actions>
      </Wrapper>
   );
};

export default IspCustomerForm;

const Wrapper = styled.div`
   width: 640px;
   max-width: 100%;
`;

const Grid = styled.div`
   display: grid;
   grid-template-columns: repeat(2, minmax(0, 1fr));
   gap: 12px 16px;
   margin-top: 4px;

   @media (max-width: 720px) {
      grid-template-columns: minmax(0, 1fr);
   }
`;

const Field = styled(SettingsInput)<{ $err?: boolean }>`
   border-color: ${({ $err }) => ($err ? colors.DANGER : colors.GRAY12)};
`;

const Err = styled.p`
   margin: 10px 0 0;
   font-size: 12px;
   color: ${colors.DANGER};
`;

const Actions = styled.div`
   display: flex;
   justify-content: flex-end;
   gap: 8px;
   margin-top: 18px;
`;

const Sel = styled.select`
   padding: 8px 10px;
   border: 1px solid ${colors.GRAY12};
   border-radius: 6px;
   font-size: 13px;
   font-family: inherit;
   color: ${colors.MAIN_DARK};
   background: var(--app-surface);
   outline: none;
   &:focus {
      border-color: ${colors.PRIMARY};
   }
`;

const Primary = styled.button`
   padding: 8px 16px;
   border-radius: 8px;
   border: none;
   background: ${colors.PRIMARY};
   color: ${colors.WHITE};
   font-size: 13px;
   font-weight: 600;
   cursor: pointer;

   &:disabled {
      opacity: 0.6;
      cursor: default;
   }
`;

const Ghost = styled.button`
   padding: 8px 16px;
   border-radius: 8px;
   border: 1px solid ${colors.GRAY12};
   background: transparent;
   color: inherit;
   font-size: 13px;
   cursor: pointer;
`;
